import { useEffect, useRef, useState, useCallback } from "react";
import { AppToolbarButton } from "./WhatsNew";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import Map from "ol/Map";
import View from "ol/View";
import { fromLonLat, toLonLat } from "ol/proj";
import { Feature } from "ol";
import { LineString, Polygon } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import Draw from "ol/interaction/Draw";
import { createBox } from "ol/interaction/Draw";
import "ol/ol.css";
import { useProject } from "./ProjectContext";
import { projectCoordsToLonLat, lonLatToUtm32n, createBaseLayers } from "./mapUtils";
import { TerrainMesh3D, type TerrainData } from "./TerrainMesh3D";

interface RawPoint {
  station: number;
  easting: number;
  northing: number;
  elevation: number;
}

interface ClipExtent {
  minEasting: number;
  maxEasting: number;
  minNorthing: number;
  maxNorthing: number;
}

const PANEL_BG = "linear-gradient(180deg, rgba(0,0,0,0.82), rgba(0,0,0,0.72))";
const PANEL_BORDER = "rgba(255,255,255,0.12)";

async function fetchRawCenterline(endpoint: string): Promise<RawPoint[]> {
  const resp = await fetch(endpoint);
  const xml = await resp.text();
  const { parseLandXML } = await import("./parseLandXML");
  const { rawPoints } = await parseLandXML(xml);
  return rawPoints;
}

type ImportTab = "kartverket" | "file";

function formatExtentSize(extent: ClipExtent): string {
  const w = extent.maxEasting - extent.minEasting;
  const h = extent.maxNorthing - extent.minNorthing;
  if (w > 1000 || h > 1000) {
    return `${(w / 1000).toFixed(1)} × ${(h / 1000).toFixed(1)} km`;
  }
  return `${w.toFixed(0)} × ${h.toFixed(0)} m`;
}

export function TerrainModelsView({ onBack }: { onBack: () => void }) {
  const project = useProject();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<Map | null>(null);
  const drawInteractionRef = useRef<Draw | null>(null);
  const extentLayerRef = useRef<VectorLayer | null>(null);

  const [rawPoints, setRawPoints] = useState<RawPoint[]>([]);
  const [activeTab, setActiveTab] = useState<ImportTab>("kartverket");
  const [clipExtent, setClipExtent] = useState<ClipExtent | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [resolution, setResolution] = useState<"10" | "1">("10");
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [terrainData, setTerrainData] = useState<TerrainData | null>(null);
  const [show3D, setShow3D] = useState(false);

  // Fetch centerline
  useEffect(() => {
    fetchRawCenterline(project.centerlineEndpoint).then(setRawPoints);
  }, [project.centerlineEndpoint]);

  // Auto-generate default extent from centerline with buffer (in UTM 32N for WCS)
  useEffect(() => {
    if (rawPoints.length < 2 || clipExtent) return;
    const utm32Coords = rawPoints.map((p) => {
      const [lon, lat] = projectCoordsToLonLat(p.easting, p.northing);
      return lonLatToUtm32n(lon, lat);
    });
    const eastings = utm32Coords.map((c) => c[0]);
    const northings = utm32Coords.map((c) => c[1]);
    const buffer = 500; // 500m buffer around tunnel
    setClipExtent({
      minEasting: Math.min(...eastings) - buffer,
      maxEasting: Math.max(...eastings) + buffer,
      minNorthing: Math.min(...northings) - buffer,
      maxNorthing: Math.max(...northings) + buffer,
    });
  }, [rawPoints]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = new Map({
      target: mapRef.current,
      layers: createBaseLayers(),
      view: new View({
        center: fromLonLat([7.8, 60.9]),
        zoom: 13,
      }),
      controls: [],
    });
    mapInstance.current = map;

    // Extent drawing layer
    const extentSource = new VectorSource();
    const extentLayer = new VectorLayer({
      source: extentSource,
      style: new Style({
        stroke: new Stroke({ color: "#ff9800", width: 2, lineDash: [8, 4] }),
        fill: new Fill({ color: "rgba(255, 152, 0, 0.08)" }),
      }),
    });
    map.addLayer(extentLayer);
    extentLayerRef.current = extentLayer;

    return () => {
      map.setTarget(undefined);
      mapInstance.current = null;
    };
  }, []);

  // Draw centerline on map and fit view
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || rawPoints.length < 2) return;

    const coords = rawPoints.map((p) => {
      const [lon, lat] = projectCoordsToLonLat(p.easting, p.northing);
      return fromLonLat([lon, lat]);
    });

    const lineFeature = new Feature({ geometry: new LineString(coords) });
    const vectorSource = new VectorSource({ features: [lineFeature] });
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({ color: "#4fc3f7", width: 3 }),
      }),
    });

    map.addLayer(vectorLayer);

    const extent = vectorSource.getExtent();
    if (extent && isFinite(extent[0])) {
      map.getView().fit(extent, { padding: [80, 80, 80, 80], maxZoom: 15 });
    }

    return () => {
      map.removeLayer(vectorLayer);
    };
  }, [rawPoints]);

  // Draw clip extent rectangle on map
  useEffect(() => {
    const layer = extentLayerRef.current;
    if (!layer || !clipExtent) return;

    const source = layer.getSource()!;
    source.clear();

    const corners = [
      [clipExtent.minEasting, clipExtent.minNorthing],
      [clipExtent.maxEasting, clipExtent.minNorthing],
      [clipExtent.maxEasting, clipExtent.maxNorthing],
      [clipExtent.minEasting, clipExtent.maxNorthing],
      [clipExtent.minEasting, clipExtent.minNorthing],
    ].map(([e, n]) => {
      const [lon, lat] = projectCoordsToLonLat(e, n);
      return fromLonLat([lon, lat]);
    });

    const feature = new Feature({ geometry: new Polygon([corners]) });
    source.addFeature(feature);
  }, [clipExtent]);

  // Start drawing a custom extent
  const startDrawExtent = useCallback(() => {
    const map = mapInstance.current;
    if (!map) return;

    // Remove existing draw interaction
    if (drawInteractionRef.current) {
      map.removeInteraction(drawInteractionRef.current);
    }

    setIsDrawing(true);

    const draw = new Draw({
      type: "Circle",
      geometryFunction: createBox(),
    });

    draw.on("drawend", (e) => {
      const geom = e.feature.getGeometry() as Polygon;
      const coords = geom.getCoordinates()[0];

      // Convert map coords back to UTM
      const utmCoords = coords.map((c) => {
        const [lon, lat] = toLonLat(c);
        return lonLatToUtm32n(lon, lat);
      });

      const eastings = utmCoords.map((c) => c[0]);
      const northings = utmCoords.map((c) => c[1]);

      setClipExtent({
        minEasting: Math.min(...eastings),
        maxEasting: Math.max(...eastings),
        minNorthing: Math.min(...northings),
        maxNorthing: Math.max(...northings),
      });

      map.removeInteraction(draw);
      drawInteractionRef.current = null;
      setIsDrawing(false);
    });

    map.addInteraction(draw);
    drawInteractionRef.current = draw;
  }, []);

  const cancelDraw = useCallback(() => {
    const map = mapInstance.current;
    if (map && drawInteractionRef.current) {
      map.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }
    setIsDrawing(false);
  }, []);

  const handleImportKartverket = useCallback(async () => {
    if (!clipExtent) return;
    setFetching(true);
    setFetchError(null);
    setTerrainData(null);
    try {
      const resp = await fetch("/api/terrain/fetch-kartverket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...clipExtent, resolution }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Fetch failed");
      setTerrainData(data);
    } catch (err: any) {
      setFetchError(err.message || "Unknown error");
    } finally {
      setFetching(false);
    }
  }, [clipExtent, resolution]);

  const sidebarWidth = 340;

  return (
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      background: "#0a1628",
      color: "#f7f8fb",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        flexShrink: 0,
        padding: "12px 20px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8,
              color: "#f7f8fb",
              cursor: "pointer",
              padding: "6px 12px",
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            ← Back
          </button>
          <div>
            <div style={{ fontSize: "1rem", fontWeight: 700 }}>Terrain Models</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.45 }}>{project.name} — Import and manage terrain data</div>
          </div>
        </div>
        <AppToolbarButton />
      </header>

      {/* Body: sidebar + map */}
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* Sidebar */}
        <div style={{
          width: sidebarWidth,
          flexShrink: 0,
          borderRight: "1px solid rgba(255,255,255,0.06)",
          overflowY: "auto",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}>
          {/* Tab selector */}
          <div style={{
            display: "flex",
            gap: 4,
            background: "rgba(255,255,255,0.04)",
            borderRadius: 10,
            padding: 3,
          }}>
            {([
              { id: "kartverket" as const, label: "Kartverket" },
              { id: "file" as const, label: "Import file" },
            ]).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: "7px 10px",
                  borderRadius: 8,
                  border: "none",
                  background: activeTab === tab.id ? "rgba(255,255,255,0.12)" : "transparent",
                  color: activeTab === tab.id ? "#f7f8fb" : "rgba(255,255,255,0.4)",
                  cursor: "pointer",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  transition: "all 0.15s",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Kartverket tab */}
          {activeTab === "kartverket" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Source info */}
              <div style={{
                padding: "12px 14px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${PANEL_BORDER}`,
                fontSize: "0.75rem",
                lineHeight: 1.5,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: "0.7rem", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Data source
                </div>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>Kartverket Høydedata</span>
                </div>
                <div style={{ opacity: 0.55, fontSize: "0.7rem" }}>
                  National elevation data from the Norwegian Mapping Authority. DTM derived from airborne LiDAR scanning (NDH).
                </div>
                <div style={{ marginTop: 8, opacity: 0.4, fontSize: "0.65rem" }}>
                  hoydedata.no — CC BY 4.0
                </div>
              </div>

              {/* Resolution selector */}
              <div>
                <div style={{ fontSize: "0.7rem", fontWeight: 600, opacity: 0.5, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Resolution
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {([
                    { value: "10" as const, label: "DTM 10", desc: "10 m grid" },
                    { value: "1" as const, label: "DTM 1", desc: "1 m grid" },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setResolution(opt.value)}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: `1px solid ${resolution === opt.value ? "#ff980066" : PANEL_BORDER}`,
                        background: resolution === opt.value ? "rgba(255, 152, 0, 0.1)" : "rgba(255,255,255,0.03)",
                        color: "#f7f8fb",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ fontSize: "0.78rem", fontWeight: 600 }}>{opt.label}</div>
                      <div style={{ fontSize: "0.65rem", opacity: 0.4 }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Clip extent */}
              <div>
                <div style={{ fontSize: "0.7rem", fontWeight: 600, opacity: 0.5, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Clip extent
                </div>
                {clipExtent && (
                  <div style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid ${PANEL_BORDER}`,
                    fontSize: "0.72rem",
                    marginBottom: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ opacity: 0.5 }}>Size:</span>
                      <span style={{ fontWeight: 600 }}>{formatExtentSize(clipExtent)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ opacity: 0.5 }}>E:</span>
                      <span style={{ fontWeight: 500, fontFamily: "monospace", fontSize: "0.68rem" }}>
                        {clipExtent.minEasting.toFixed(0)} – {clipExtent.maxEasting.toFixed(0)}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ opacity: 0.5 }}>N:</span>
                      <span style={{ fontWeight: 500, fontFamily: "monospace", fontSize: "0.68rem" }}>
                        {clipExtent.minNorthing.toFixed(0)} – {clipExtent.maxNorthing.toFixed(0)}
                      </span>
                    </div>
                    {resolution === "10" && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                        <span style={{ opacity: 0.5 }}>Grid cells:</span>
                        <span style={{ fontWeight: 500, fontSize: "0.68rem" }}>
                          ~{Math.ceil((clipExtent.maxEasting - clipExtent.minEasting) / 10) * Math.ceil((clipExtent.maxNorthing - clipExtent.minNorthing) / 10)} pts
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  {isDrawing ? (
                    <button
                      type="button"
                      onClick={cancelDraw}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(255,100,100,0.4)",
                        background: "rgba(255,100,100,0.1)",
                        color: "#f7f8fb",
                        cursor: "pointer",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                      }}
                    >
                      Cancel drawing
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={startDrawExtent}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: `1px solid rgba(255, 152, 0, 0.4)`,
                        background: "rgba(255, 152, 0, 0.1)",
                        color: "#f7f8fb",
                        cursor: "pointer",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                      }}
                    >
                      Draw custom extent
                    </button>
                  )}
                </div>
              </div>

              {/* Import button */}
              <button
                type="button"
                disabled={!clipExtent || fetching}
                onClick={handleImportKartverket}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid rgba(255, 152, 0, 0.5)",
                  background: fetching ? "rgba(255, 152, 0, 0.08)" : "rgba(255, 152, 0, 0.2)",
                  color: !clipExtent || fetching ? "rgba(255,255,255,0.35)" : "#f7f8fb",
                  cursor: !clipExtent || fetching ? "not-allowed" : "pointer",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  opacity: !clipExtent ? 0.5 : 1,
                  transition: "all 0.15s",
                }}
              >
                {fetching ? "Fetching terrain data..." : "Import from Kartverket"}
              </button>
              <div style={{ fontSize: "0.65rem", opacity: 0.3, textAlign: "center" }}>
                Fetches DTM{resolution} data for the selected extent via Kartverket WCS
              </div>

              {/* Fetch result / error */}
              {fetchError && (
                <div style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "rgba(255, 80, 80, 0.1)",
                  border: "1px solid rgba(255, 80, 80, 0.3)",
                  fontSize: "0.72rem",
                  color: "#ff8888",
                }}>
                  {fetchError}
                </div>
              )}
              {terrainData && (
                <div style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "rgba(76, 175, 80, 0.1)",
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                  fontSize: "0.72rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}>
                  <div style={{ fontWeight: 600, color: "#81c784" }}>Terrain imported</div>
                  <div style={{ opacity: 0.7 }}>{terrainData.width} x {terrainData.height} grid</div>
                  <div style={{ opacity: 0.7 }}>
                    Elevation: {terrainData.stats.min} – {terrainData.stats.max} m (avg {terrainData.stats.mean} m)
                  </div>
                </div>
              )}
            </div>
          )}

          {/* File import tab */}
          {activeTab === "file" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{
                padding: "12px 14px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${PANEL_BORDER}`,
                fontSize: "0.75rem",
                lineHeight: 1.5,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: "0.7rem", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Supported formats
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    { format: "LandXML", desc: "Surface mesh (.xml)" },
                    { format: "GeoTIFF", desc: "Raster DEM (.tif)" },
                    { format: "OBJ / PLY", desc: "3D mesh (.obj, .ply)" },
                  ].map((f) => (
                    <div key={f.format} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{f.format}</span>
                        <span style={{ opacity: 0.4, marginLeft: 6 }}>{f.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                disabled
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: `1px solid ${PANEL_BORDER}`,
                  background: "rgba(255,255,255,0.04)",
                  color: "rgba(255,255,255,0.3)",
                  cursor: "not-allowed",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  opacity: 0.5,
                }}
              >
                Upload terrain file — coming soon
              </button>

              <button
                type="button"
                disabled
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: `1px solid ${PANEL_BORDER}`,
                  background: "rgba(255,255,255,0.04)",
                  color: "rgba(255,255,255,0.3)",
                  cursor: "not-allowed",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  opacity: 0.5,
                }}
              >
                Import LandXML surface — coming soon
              </button>
            </div>
          )}
        </div>

        {/* Map / 3D view */}
        <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
          {/* View toggle */}
          {terrainData && (
            <div style={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 10,
              display: "flex",
              gap: 4,
              background: "rgba(0,0,0,0.7)",
              borderRadius: 8,
              padding: 3,
              border: "1px solid rgba(255,255,255,0.15)",
            }}>
              {(["map", "3d"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setShow3D(mode === "3d")}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: "none",
                    background: (mode === "3d") === show3D ? "rgba(255,255,255,0.15)" : "transparent",
                    color: (mode === "3d") === show3D ? "#f7f8fb" : "rgba(255,255,255,0.4)",
                    cursor: "pointer",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                  }}
                >
                  {mode === "map" ? "Map" : "3D"}
                </button>
              ))}
            </div>
          )}

          {/* Map view */}
          <div ref={mapRef} style={{
            width: "100%",
            height: "100%",
            display: show3D ? "none" : "block",
          }} />

          {/* 3D terrain preview */}
          {show3D && terrainData && (
            <Canvas
              gl={{ alpha: false, antialias: true }}
              camera={{ fov: 50, near: 0.1, far: 50000, position: [0, 500, 800] }}
              style={{ width: "100%", height: "100%", background: "#0a1628" }}
            >
              <ambientLight intensity={0.4} />
              <directionalLight position={[200, 500, 300]} intensity={1} />
              <directionalLight position={[-200, 300, -100]} intensity={0.3} />
              <group rotation={[-Math.PI / 2, 0, 0]}>
                <TerrainMesh3D
                  terrain={terrainData}
                  originOffset={{
                    x: (terrainData.extent.minEasting + terrainData.extent.maxEasting) / 2,
                    y: (terrainData.extent.minNorthing + terrainData.extent.maxNorthing) / 2,
                    z: terrainData.stats.mean,
                  }}
                />
              </group>
              <OrbitControls
                enableDamping
                dampingFactor={0.1}
                maxPolarAngle={Math.PI / 2}
              />
            </Canvas>
          )}

          {/* Drawing hint */}
          {isDrawing && !show3D && (
            <div style={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              padding: "8px 16px",
              borderRadius: 10,
              background: "rgba(255, 152, 0, 0.9)",
              color: "#000",
              fontSize: "0.8rem",
              fontWeight: 700,
              pointerEvents: "none",
            }}>
              Click and drag to draw clip extent
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
